import type { HttpTransport } from '@mangovoice/api-client'

import type {
  PaginatedScheduleList,
  ScheduleEventsListParams,
  ScheduleListParams,
} from '../types/api.types'
import type {
  Schedule,
  ScheduleCreate,
  ScheduleEvent,
  ScheduleEventCreate,
  ScheduleEventId,
  ScheduleEventUpdate,
  ScheduleHoliday,
  ScheduleId,
  ScheduleRetrieve,
  ScheduleUpdate,
} from '../types/schedule.types'
import { SCHEDULE_ENDPOINTS } from './endpoints'

interface ScheduleEndpoints {
  LIST: string
  DETAIL: (id: ScheduleId) => string
  CREATE: string
  UPDATE: (id: ScheduleId) => string
  DELETE: (id: ScheduleId) => string
  INVALIDATE_CACHE: (legacyScheduleId: number) => string
  EVENTS_LIST: (scheduleId: ScheduleId) => string
  EVENTS_CREATE: (scheduleId: ScheduleId) => string
  EVENTS_DETAIL: (scheduleId: ScheduleId, eventId: ScheduleEventId) => string
  EVENTS_UPDATE: (scheduleId: ScheduleId, eventId: ScheduleEventId) => string
  EVENTS_DELETE: (scheduleId: ScheduleId, eventId: ScheduleEventId) => string
  HOLIDAYS_LIST: string
}

/**
 * Create schedule API functions from a client
 */
export const createScheduleApi = (
  client: HttpTransport,
  endpoints: ScheduleEndpoints = SCHEDULE_ENDPOINTS,
) => ({
  // Schedule CRUD
  getSchedules: (params?: ScheduleListParams) =>
    client.get<PaginatedScheduleList>(
      endpoints.LIST,
      params as Record<string, unknown>,
    ),

  getSchedule: (id: ScheduleId) =>
    client.get<ScheduleRetrieve>(endpoints.DETAIL(id)),

  createSchedule: (
    data: ScheduleCreate,
    options?: { defaultWeeklyHours?: boolean },
  ) => {
    const url = options?.defaultWeeklyHours
      ? `${endpoints.CREATE}?default_weekly_hours=1`
      : endpoints.CREATE
    return client.post<Schedule>(url, data)
  },

  updateSchedule: (id: ScheduleId, data: ScheduleUpdate) =>
    client.put<Schedule>(endpoints.UPDATE(id), data),

  patchSchedule: (id: ScheduleId, data: Partial<ScheduleUpdate>) =>
    client.patch<Schedule>(endpoints.UPDATE(id), data),

  deleteSchedule: (id: ScheduleId) =>
    client.delete<unknown>(endpoints.DELETE(id)),

  invalidateScheduleCache: (legacyScheduleId: number) =>
    client.put<void>(endpoints.INVALIDATE_CACHE(legacyScheduleId)),

  // Schedule Events CRUD
  getScheduleEvents: (
    scheduleId: ScheduleId,
    params?: Omit<ScheduleEventsListParams, 'schedule_id'>,
  ) =>
    client.get<ScheduleEvent[]>(
      endpoints.EVENTS_LIST(scheduleId),
      params as Record<string, unknown>,
    ),

  getScheduleEvent: (scheduleId: ScheduleId, eventId: ScheduleEventId) =>
    client.get<ScheduleEvent>(endpoints.EVENTS_DETAIL(scheduleId, eventId)),

  createScheduleEvent: (scheduleId: ScheduleId, data: ScheduleEventCreate) =>
    client.post<ScheduleEvent>(endpoints.EVENTS_CREATE(scheduleId), data),

  updateScheduleEvent: (
    scheduleId: ScheduleId,
    eventId: ScheduleEventId,
    data: ScheduleEventUpdate,
  ) =>
    client.put<ScheduleEvent>(
      endpoints.EVENTS_UPDATE(scheduleId, eventId),
      data,
    ),

  patchScheduleEvent: (
    scheduleId: ScheduleId,
    eventId: ScheduleEventId,
    data: Partial<ScheduleEventUpdate>,
  ) =>
    client.patch<ScheduleEvent>(
      endpoints.EVENTS_UPDATE(scheduleId, eventId),
      data,
    ),

  deleteScheduleEvent: (scheduleId: ScheduleId, eventId: ScheduleEventId) =>
    client.delete<void>(endpoints.EVENTS_DELETE(scheduleId, eventId)),

  // Preset Holidays
  getScheduleHolidays: () =>
    client.get<ScheduleHoliday[]>(endpoints.HOLIDAYS_LIST),
})

export type ScheduleApi = ReturnType<typeof createScheduleApi>
